import { OMNIWOUND_ROLES } from '@omniwound/auth-contract';
import {
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Users,
} from 'lucide-react';

/**
 * Shared navigation model for the desktop sidebar and the mobile tab bar.
 *
 * `id` is a stable, locale-independent slug. It drives the `data-testid`s the
 * e2e suite selects on (`sidebar-nav-qa`), so it must never be derived from
 * `labelKey` or from translated text.
 */
export type NavItem = {
  id: string;
  labelKey: string;
  to: string;
  icon: typeof Users;
};

/**
 * Which workspaces the current account may be shown — the input the nav model
 * renders from. `useNavEligibility` derives it (ADR 0328).
 */
export type NavEligibility = {
  patientIntake: boolean;
  qaReview: boolean;
};

/**
 * A group is a rendering unit, not a labelled section: the sidebar separates
 * groups by spacing alone, so there is no `labelKey` here. `id` still keys the
 * React list and names the group in tests.
 */
export type NavGroup = {
  id: string;
  items: NavItem[];
};

const CareGroupId = 'care';

/**
 * The clinical workspaces. They follow the access gate's admission, not a
 * capability: every account the gate admits that is not a Staff account keeps
 * them, which is why they hang off `patientIntake` alone (ADR 0328).
 */
const ClinicalItems: readonly NavItem[] = [
  { id: 'patients', labelKey: 'nav.patients', to: '/patients', icon: Users },
  {
    id: 'referrals',
    labelKey: 'nav.referrals',
    to: '/referrals',
    icon: ClipboardList,
  },
  {
    id: 'visits',
    labelKey: 'nav.visits',
    to: '/visits',
    icon: CalendarClock,
  },
];

/**
 * The QA work queue. It is a Staff workspace whose reads the server denies to
 * anyone else, so it is offered only when the account's own application-access
 * decision carries QA Review (ADR 0328).
 */
const QaItem: NavItem = {
  id: 'qa',
  labelKey: 'nav.qa',
  to: '/qa',
  icon: ClipboardCheck,
};

function careItemsFor(eligibility: NavEligibility): NavItem[] {
  const items: NavItem[] = [];
  if (eligibility.patientIntake) {
    items.push(...ClinicalItems);
  }
  if (eligibility.qaReview) {
    items.push(QaItem);
  }
  return items;
}

/** Nav groups visible to the current account (empty groups are not emitted). */
export function navGroupsFor(eligibility: NavEligibility): NavGroup[] {
  const groups: NavGroup[] = [];
  const careItems = careItemsFor(eligibility);
  if (careItems.length > 0) {
    groups.push({ id: CareGroupId, items: careItems });
  }
  return groups;
}

/** Flat item list, for surfaces (the tab bar) that render no group chrome. */
export function navItemsFor(eligibility: NavEligibility): NavItem[] {
  return navGroupsFor(eligibility).flatMap((group) => group.items);
}

/** True when the principal's realm roles include the OmniWound admin role. */
export function isAdminPrincipal(realmRoles: readonly string[]): boolean {
  return realmRoles.includes(OMNIWOUND_ROLES.ADMIN);
}

/**
 * The nav path a route belongs to. A patient profile opened from Referrals
 * (`?from=referrals`) belongs to the Referrals section, not Patients, so the
 * nav keeps the originating section highlighted.
 */
export function resolveActivePath(
  pathname: string,
  from: string | undefined,
): string {
  if (from === 'referrals' && pathname.startsWith('/patients/')) {
    return '/referrals';
  }
  return pathname;
}
